import { Check, Search } from "lucide-react";
import { Reveal } from "./Reveal";

const scrollToGetStarted = (e) => {
  e.preventDefault();
  const target = document.querySelector("#get-started");
  if (!target) return;
  if (window.__lenis) {
    // Numeric target: Lenis's selector resolution lands ~96px short on this section.
    const top = target.getBoundingClientRect().top + window.scrollY - 90;
    window.__lenis.scrollTo(top);
  } else {
    target.scrollIntoView({ behavior: "smooth" });
  }
};

const Feature = ({ children }) => (
  <li className="flex items-start gap-3 text-base text-crave-cream/85">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-crave-gold/15 text-crave-gold">
      <Check className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
    {children}
  </li>
);

const TIERS = [
  {
    testId: "pricing-card-guest",
    ctaTestId: "pricing-guest-cta",
    name: "Guest",
    price: "$0",
    cadence: "/ no account needed",
    features: ["Unlimited Discover search"],
    note: "Create an account to save places, add friends, and send invitations.",
    cta: null,
    featured: false,
  },
  {
    testId: "pricing-card-free",
    ctaTestId: "pricing-free-cta",
    name: "Free",
    price: "$0",
    cadence: "/ forever",
    features: [
      "Unlimited Discover search",
      "3 Places (Saved + Visited total)",
      "3 Friends",
      "3 Invitations",
    ],
    note: null,
    cta: "Get Early Access",
    featured: false,
  },
  {
    testId: "pricing-card-premium",
    ctaTestId: "pricing-premium-cta",
    name: "Premium",
    price: "$2.99",
    cadence: "/ month",
    subPrice: "or $15.99 first year · then $19.99/year",
    features: [
      "Unlimited Discover search",
      "50 Places (Saved + Visited total)",
      "50 Friends",
      "50 Invitations",
    ],
    note: "Premium subscriptions and eligible free trials are activated in the app.",
    cta: "Get Early Access",
    featured: true,
  },
];

export const Pricing = () => (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="scroll-mt-24 border-y border-crave-border bg-[#181514] py-24 md:py-32"
  >
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Reveal className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Pricing</p>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-crave-cream sm:text-5xl">
          Start free. Upgrade when you need more room.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-crave-muted">
          Every Premium plan starts with a 30-day free trial. Cancel anytime — no hard feelings,
          only full bellies.
        </p>
      </Reveal>

      <div className="mx-auto mt-16 grid max-w-5xl grid-cols-1 items-stretch gap-8 md:grid-cols-3">
        {TIERS.map((tier, i) => (
          <Reveal key={tier.name} delay={i * 0.08} className="h-full">
            <div
              data-testid={tier.testId}
              className={`relative flex h-full flex-col rounded-3xl p-8 transition-colors duration-500 ${
                tier.featured
                  ? "border-2 border-crave-gold bg-[#221d19] shadow-[0_0_80px_-20px_rgba(212,162,74,0.45)]"
                  : "border border-crave-border bg-crave-card hover:border-crave-cream/25"
              }`}
            >
              {tier.featured && (
                <span className="absolute -top-4 left-8 rounded-full bg-crave-gold px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-crave-bg">
                  30-day free trial
                </span>
              )}
              <h3
                className={`font-display text-2xl font-medium tracking-tight ${
                  tier.featured ? "text-crave-gold" : "text-crave-cream"
                }`}
              >
                {tier.name}
              </h3>
              <p className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-5xl font-medium text-crave-cream">{tier.price}</span>
                <span className="text-sm text-crave-muted">{tier.cadence}</span>
              </p>
              {tier.subPrice && (
                <p className="mt-2 text-sm font-medium text-crave-gold">{tier.subPrice}</p>
              )}
              <ul className="mt-8 flex flex-col gap-4">
                {tier.features.map((f) => (
                  <Feature key={f}>{f}</Feature>
                ))}
              </ul>
              {tier.cta && (
                <a
                  href="#get-started"
                  onClick={scrollToGetStarted}
                  data-testid={tier.ctaTestId}
                  className={`mt-auto block rounded-full px-6 py-3.5 text-center text-sm font-semibold transition-colors duration-300 ${
                    tier.featured
                      ? "bg-crave-gold font-bold text-crave-bg hover:bg-crave-cream"
                      : "border border-white/20 text-crave-cream hover:border-crave-cream/60"
                  } mt-10`}
                >
                  {tier.cta}
                </a>
              )}
              {tier.note && (
                <p className="mt-4 text-sm leading-relaxed text-crave-muted">{tier.note}</p>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <p
          data-testid="pricing-billing-note"
          className="mx-auto mt-10 max-w-xl text-center text-xs leading-relaxed text-crave-muted"
        >
          Your selected subscription begins billing after your 30-day free trial unless canceled
          before the trial ends. Free trials are available to eligible first-time Premium
          subscribers.
        </p>
        <p
          data-testid="pricing-disclaimer"
          className="mx-auto mt-4 flex max-w-xl items-center justify-center gap-2 text-center text-xs leading-relaxed text-crave-muted"
        >
          <Search className="h-3.5 w-3.5 shrink-0 text-crave-gold" aria-hidden="true" />
          We're growing with our community. Features and plan allowances may evolve as Foodie Crave
          develops. We'll communicate important changes in advance.
        </p>
      </Reveal>
    </div>
  </section>
);
